import cn from 'classnames';
import { useContext, useEffect, useState, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';

import DiscountTag from 'components/common/DiscountTag';
import Heading from 'components/common/Heading';
import StrikethroughSkeleton from 'components/common/StrikethroughSkeleton';
import Text from 'components/common/Text';
import { BasketContext, UserContext, VoucherContext } from 'components/signUpFunnel/Contexts';
import FreeForLifeBanner from 'components/signUpFunnel/FreeForLifeBanner';
import MaxDiscountMessage from 'components/signUpFunnel/shared/MaxDiscountMessage';
import { BrandContext, HOME_CHEF, TEMPO } from 'context/BrandContext';
import { hasServingRange } from 'helpers/servings';
import useBrand from 'hooks/useBrand';
import { KETO } from 'lib/constants';
import styles from 'styles/components/signUpFunnel/box/OrderSummary';

import FirstWeekDisclaimer from './FirstWeekDisclaimer';
import PricePerServing from './PricePerServing';
import RecommendedMealsTip from './RecommendedMealsTip';
import TempoOrderSummaryPPS from './TempoOrderSummaryPPS';

const OrderSummary = ({
  box,
  servings,
  weeklyMeals,
  showRecommendedMealsTip = false,
  boxSignupAnswers = [],
}) => {
  const { setBasket } = useContext(BasketContext);
  const { brand } = useContext(BrandContext);
  const { user } = useContext(UserContext);
  const { voucher } = useContext(VoucherContext);

  const isHomeChef = useBrand(HOME_CHEF);
  const isTempo = useBrand(TEMPO);

  const [includesServingRange, setIncludesServingRange] = useState(hasServingRange(box));

  useEffect(() => {
    setIncludesServingRange(hasServingRange(box));
  }, [box]);

  const userHasKetoOptIn = useMemo(() => {
    return user?.optIns?.includes(KETO);
  }, [user?.optIns]);

  const hasSmsDiscount = user?.smsDiscount && box?.smsIncentivePromo;

  const SummarySkeleton = () => {
    return (
      <div className={cn('pt-32 text-center', styles.orderSummary)}>
        <section className="m-auto max-w-xl pl-12 pr-6">
          <div className="hidden w-1/4 rounded-md bp2:inline">
            <Skeleton count={3} className="my-6" height={32} />
          </div>
        </section>
      </div>
    );
  };

  useEffect(() => {
    setBasket({ total: box?.totalFormatted, servings: servings, meals: weeklyMeals });
  }, [weeklyMeals, servings]);

  const ServingsPerMeal = () => {
    return (
      <div className="flex">
        <Text
          className="flex-1 pb-2 text-justify"
          htmlElement="dt"
          brandStylesOverrides="text text-secondary"
        >
          Servings Per Meal
        </Text>
        <Text
          className="pb-2"
          htmlElement="dd"
          data-testid="servings-element"
          brandStylesOverrides="textMd text-secondary"
        >
          {servings}
        </Text>
      </div>
    );
  };

  const MealsPerWeek = () => {
    return (
      <div className="flex">
        <Text
          htmlElement="dt"
          className={cn('flex-1 pb-4 text-justify', styles[brand], styles.textModule)}
          brandStylesOverrides="text text-secondary"
        >
          Meals Per Week
        </Text>
        <Text htmlElement="dd">
          <Text
            className="flex-1"
            data-testid="meals-element"
            htmlElement="span"
            brandStylesOverrides="textMd text-secondary"
          >
            {weeklyMeals}
          </Text>
        </Text>
      </div>
    );
  };

  return (
    <>
      {box ? (
        <section className={cn('pb-12 pt-6 text-center bp2:pt-0', styles.orderSummary)}>
          <Heading
            className={cn('mb-5 text-center text-2xl', {
              'font-bold': isHomeChef,
            })}
            brandStylesOverrides="h4"
          >
            Order Summary
          </Heading>
          <div className="max-w-xl bp2:pl-4 bp2:pr-2 bp3:pl-12 bp3:pr-6">
            {isHomeChef && (
              <>
                <PricePerServing box={box} includesServingRange={includesServingRange} />
                <ServingsPerMeal />
                <MealsPerWeek />
              </>
            )}

            <div
              className={cn('bg flex flex-col border-solid border-charcoal-60 text-left', {
                'border-b pb-4': userHasKetoOptIn,
              })}
            >
              {isTempo && <TempoOrderSummaryPPS box={box} classes="text-tempo-black-100" />}
              {!includesServingRange ||
                (!isTempo && (
                  <Text brandStylesOverrides="textSm text-light" className="pb-2">
                    You can change this at any time
                  </Text>
                ))}
            </div>
            {!userHasKetoOptIn && (
              <div className="flex flex-col pb-2 pt-2 text-left">
                <div className="flex">
                  <Text
                    htmlElement="dt"
                    brandStylesOverrides="text text-secondary"
                    className={cn('flex-1 pb-4 pt-4', styles[brand], styles.textModule)}
                  >
                    Subtotal
                  </Text>
                  <Text htmlElement="dd" className="flex items-center whitespace-nowrap">
                    {box?.promoApplied &&
                    box?.subtotalFormatted !== box?.promo.subtotalFormatted ? (
                      <>
                        {hasSmsDiscount && <DiscountTag discountText="5% OFF" />}
                        <DiscountTag discountText={box.promo.displayedDiscount} />
                        <StrikethroughSkeleton
                          strikethroughText={box?.subtotalFormatted}
                          textDisplayed={box?.promo.subtotalFormatted}
                          ariaLabel="subtotal"
                        />
                      </>
                    ) : hasSmsDiscount ? (
                      <>
                        <DiscountTag discountText="5% OFF" />
                        <StrikethroughSkeleton
                          strikethroughText={box?.subtotalFormatted}
                          textDisplayed={box?.smsIncentivePromo.subtotalFormatted}
                          ariaLabel="subtotal"
                        />
                      </>
                    ) : (
                      <Text brandStylesOverrides="textMd text-secondary" htmlElement="span">
                        {box?.subtotalFormatted}
                      </Text>
                    )}
                  </Text>
                </div>
                <MaxDiscountMessage maxDiscountMessage={box?.promo?.maxDiscountMessage} />
                <div className="flex">
                  <Text
                    brandStylesOverrides="text text-secondary"
                    className={cn('flex-1 pb-4', styles[brand], styles.textModule)}
                    htmlElement="dt"
                  >
                    Shipping
                  </Text>
                  <Text htmlElement="dd">
                    {box?.shippingPromoApplied ? (
                      <StrikethroughSkeleton
                        strikethroughText={box?.promo.shippingPromo.estimatedShippingFormatted}
                        textDisplayed={box?.promo.shippingPromo.shippingPromoText}
                      />
                    ) : (
                      <Text
                        brandStylesOverrides="textMd text-secondary"
                        className="float-right"
                        htmlElement="span"
                      >
                        {box?.shippingFormatted}
                      </Text>
                    )}
                  </Text>
                </div>
              </div>
            )}
            {voucher && voucher.freeForLifeDiscounts && voucher.freeForLifeDiscounts.length > 0 && (
              <div className="mb-4 flex flex-col gap-2">
                {voucher.freeForLifeDiscounts.map((discount, index) => {
                  return <FreeForLifeBanner text={discount.bannerText} key={index} />;
                })}
              </div>
            )}
            <FirstWeekDisclaimer box={box} includesServingRange={includesServingRange} />
            {showRecommendedMealsTip && <RecommendedMealsTip boxSignupAnswers={boxSignupAnswers} />}
          </div>
        </section>
      ) : (
        <SummarySkeleton />
      )}
    </>
  );
};

OrderSummary.propTypes = {
  servings: PropTypes.number.isRequired,
  weeklyMeals: PropTypes.number.isRequired,
  box: PropTypes.shape({
    subtotalFormatted: PropTypes.string.isRequired,
    subtotalCents: PropTypes.number.isRequired,
    totalFormatted: PropTypes.string.isRequired,
    totalCents: PropTypes.number.isRequired,
    promoApplied: PropTypes.bool.isRequired,
    shippingPromoApplied: PropTypes.bool,
    shippingFormatted: PropTypes.string.isRequired,
    shippingCents: PropTypes.number.isRequired,
    taxFormatted: PropTypes.string.isRequired,
    taxCents: PropTypes.number.isRequired,
    pricePerServingFormatted: PropTypes.string.isRequired,
    pricePerServingCents: PropTypes.number.isRequired,
    afterSignUpTotalFormatted: PropTypes.string.isRequired,
    afterSignUpTotalCents: PropTypes.number.isRequired,
    pricePerServingLow: PropTypes.number,
    pricePerServingHigh: PropTypes.number,
    smsIncentivePromo: PropTypes.shape({
      subtotalCents: PropTypes.number,
      subtotalFormatted: PropTypes.string,
    }),
    promo: PropTypes.shape({
      subtotalCents: PropTypes.number,
      subtotalFormatted: PropTypes.string,
      pricePerServingFormatted: PropTypes.string,
      shippingPromo: PropTypes.shape({
        estimatedShippingFormatted: PropTypes.string,
        shippingPromoText: PropTypes.string,
      }),

      maxDiscountMessage: PropTypes.string,
    }),
  }),
  showRecommendedMealsTip: PropTypes.bool,
  boxSignupAnswers: PropTypes.array,
};

export default OrderSummary;
